/**
 * BUG #40 — Kickback Item / Offer Evidence
 *
 * Step 1 section of the Create Kickback wizard. Collects real product /
 * service / company / offer proof — NOT the artistic Kickback artwork
 * (that's Step 2). Data is persisted under the `offer_evidence` object on
 * the portfolio document so it is fully separated from Step 2 artwork.
 *
 * Upload storage: uses the private-evidence endpoint `/api/nft/upload-doc`
 * which persists uploads as `nft_assets.kind = 'evidence'` in durable
 * Emergent Object Storage. Owner-gated retrieval via
 * `/api/nft/file/{id}` (AuthImage). Evidence assets are excluded from
 * `kind IN ('artwork','ai_artwork','cover')` gallery queries so they
 * NEVER appear in Step 2 / Artwork Library / NFTGallery.
 */
import React, { useCallback, useState } from 'react';
import { UploadCloud, X, FileText } from 'lucide-react';
import theme from '../theme';
import AuthImage from './AuthImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const OFFER_TYPES = [
  { value: 'physical_product',   label: 'Physical Product' },
  { value: 'digital_product',    label: 'Digital Product' },
  { value: 'service_program',    label: 'Service / Course / Program' },
  { value: 'company_employee',   label: 'Company / Employee Kickback' },
  { value: 'event_experience',   label: 'Event / Experience' },
  { value: 'other',              label: 'Other' },
];

// Field definitions per offer type. `type` = 'text' | 'textarea'.
const FIELDS_BY_OFFER = {
  physical_product: [
    { key: 'product_name',              label: 'Product Name' },
    { key: 'sku',                       label: 'SKU or Internal Product Code' },
    { key: 'manufacturer',              label: 'Manufacturer / Brand' },
    { key: 'origin_country',            label: 'Origin Country' },
    { key: 'materials',                 label: 'Materials' },
    { key: 'production_date',           label: 'Production Date or Drop Date' },
    { key: 'product_description',       label: 'Product Description',       type: 'textarea' },
    { key: 'unique_markings',           label: 'Unique Markings / Identifying Features', type: 'textarea' },
    { key: 'authenticity_notes',        label: 'Authenticity Notes',        type: 'textarea' },
    { key: 'patent_numbers',            label: 'Patent Numbers or N/A' },
  ],
  digital_product: [
    { key: 'digital_product_name',      label: 'Digital Product Name' },
    { key: 'product_type',              label: 'Product Type' },
    { key: 'access_method',             label: 'Access Method' },
    { key: 'version_release',           label: 'Version / Release Name' },
    { key: 'creator_company_name',      label: 'Creator / Company Name' },
    { key: 'delivery_notes',            label: 'Delivery Notes',            type: 'textarea' },
    { key: 'utility_access_policy',     label: 'Utility or Access Policy',  type: 'textarea' },
  ],
  service_program: [
    { key: 'service_program_name',      label: 'Service or Program Name' },
    { key: 'provider_name',             label: 'Provider Name' },
    { key: 'service_description',       label: 'Service Description',       type: 'textarea' },
    { key: 'access_method',             label: 'Access Method' },
    { key: 'included_benefits',         label: 'Included Benefits',         type: 'textarea' },
    { key: 'eligibility_rules',         label: 'Eligibility Rules',         type: 'textarea' },
    { key: 'delivery_timeline',         label: 'Delivery Timeline' },
    { key: 'utility_access_policy',     label: 'Utility or Access Policy',  type: 'textarea' },
  ],
  company_employee: [
    { key: 'company_name',              label: 'Company Name' },
    { key: 'program_name',              label: 'Program Name' },
    { key: 'program_description',       label: 'Program Description',       type: 'textarea' },
    { key: 'employee_eligibility',      label: 'Employee or Member Eligibility Notes', type: 'textarea' },
    { key: 'internal_program_rules',    label: 'Internal Program Rules',    type: 'textarea' },
    { key: 'reward_benefit_policy',     label: 'Reward / Benefit Policy',   type: 'textarea' },
  ],
  event_experience: [
    { key: 'event_name',                label: 'Event Name' },
    { key: 'event_type',                label: 'Event Type' },
    { key: 'location_or_access',        label: 'Location or Access Method' },
    { key: 'event_date_window',         label: 'Event Date or Window' },
    { key: 'access_rules',              label: 'Access Rules',              type: 'textarea' },
    { key: 'included_benefits',         label: 'Included Benefits',         type: 'textarea' },
    { key: 'limitations_restrictions',  label: 'Limitations or Restrictions', type: 'textarea' },
  ],
  other: [
    { key: 'offer_name',                label: 'Offer Name' },
    { key: 'offer_description',         label: 'Offer Description',         type: 'textarea' },
    { key: 'organization_creator_name', label: 'Organization / Creator Name' },
    { key: 'eligibility_access_notes',  label: 'Eligibility / Access Notes', type: 'textarea' },
    { key: 'additional_verification_notes', label: 'Additional Verification Notes', type: 'textarea' },
  ],
};

// Upload buckets: keyed group name → label + accepted MIME extensions.
const UPLOAD_GROUPS = [
  { key: 'product_offer_images',       label: 'Product / Offer Images',           accept: 'image/jpeg,image/png,image/webp' },
  { key: 'unique_markings_images',     label: 'Unique Markings or Proof Images',  accept: 'image/jpeg,image/png,image/webp' },
  { key: 'design_placement_images',    label: 'Design Placement / Diagram Images', accept: 'image/jpeg,image/png,image/webp' },
  { key: 'supporting_documents',       label: 'Supporting Documents or Screenshots', accept: 'image/jpeg,image/png,image/webp,application/pdf' },
];

function boxStyle() {
  return {
    border: `2px dashed ${theme.colors.borders}`,
    borderRadius: theme.radius.sm,
    padding: '14px',
    background: theme.colors.bg.secondary,
  };
}

async function uploadFile(file) {
  const token = localStorage.getItem('token');
  const fd = new FormData();
  fd.append('file', file);
  // All evidence uploads (images and PDFs) go through the evidence-tagged
  // endpoint so the resulting asset gets `kind='evidence'`. This prevents
  // Step 2 "From Gallery" (which now filters `kind != evidence`) from
  // surfacing private support material.
  const endpoint = `${BACKEND_URL}/api/nft/upload-doc`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) throw new Error('upload_failed');
  return res.json();
}

function GroupUploader({ group, files, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const onPick = useCallback(async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setBusy(true); setErr('');
    const uploaded = [];
    for (const f of list) {
      try {
        const r = await uploadFile(f);
        // Accept various response shapes but always store the minimal
        // durable pointer: {file_id, url, filename, content_type}.
        uploaded.push({
          file_id: r.file_id || r.id,
          url: r.url,
          filename: r.filename || f.name,
          content_type: r.content_type || f.type,
        });
      } catch (_) {
        setErr('One or more files failed to upload');
      }
    }
    onChange([...(files || []), ...uploaded]);
    setBusy(false);
    e.target.value = '';
  }, [files, onChange]);

  const remove = (fid) => onChange((files || []).filter(f => (f.file_id || f.id) !== fid));

  return (
    <div data-testid={`evidence-group-${group.key}`} style={{ marginBottom: 12 }}>
      <label style={{
        display: 'block', fontSize: 13, fontWeight: 600,
        color: theme.colors.text.primary, marginBottom: 6,
      }}>{group.label}</label>
      <div style={boxStyle()}>
        <input
          type="file"
          multiple
          accept={group.accept}
          onChange={onPick}
          data-testid={`evidence-input-${group.key}`}
          style={{ display: 'block', fontSize: 12, marginBottom: 8 }}
        />
        {busy && <p style={{ fontSize: 12, color: theme.colors.text.muted }}>Uploading…</p>}
        {err && <p style={{ fontSize: 12, color: theme.colors.status.error }}>{err}</p>}
        {(files || []).length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginTop: 8 }}>
            {(files || []).map((f) => {
              const isPdf = (f.content_type === 'application/pdf') || /\.pdf$/i.test(f.filename || '');
              const fid = f.file_id || f.id;
              return (
                <div key={fid} data-testid={`evidence-file-${group.key}-${fid}`}
                  style={{
                    position: 'relative', padding: 6,
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm, background: '#FFF',
                  }}>
                  {isPdf ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 80, color: theme.colors.text.muted }}>
                      <FileText size={26} /> <span style={{ fontSize: 10, marginTop: 4 }}>PDF</span>
                    </div>
                  ) : (
                    <AuthImage
                      url={f.url}
                      alt={f.filename}
                      style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: theme.radius.sm }}
                    />
                  )}
                  <div style={{ fontSize: 10, color: theme.colors.text.muted, textAlign: 'center', marginTop: 4, wordBreak: 'break-word' }}>
                    {f.filename}
                  </div>
                  <button type="button" onClick={() => remove(fid)}
                    data-testid={`evidence-remove-${group.key}-${fid}`}
                    style={{
                      position: 'absolute', top: 2, right: 2,
                      background: 'rgba(0,0,0,0.65)', color: '#FFF', border: 'none',
                      borderRadius: '50%', width: 18, height: 18, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                    <X size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function KickbackOfferEvidence({ value, onChange }) {
  const evidence = value || { offer_type: '', fields: {}, uploads: {} };
  const type = evidence.offer_type || '';
  const fields = evidence.fields || {};
  const uploads = evidence.uploads || {};

  const setType = (t) => onChange({ ...evidence, offer_type: t });
  const setField = (k, v) => onChange({ ...evidence, fields: { ...fields, [k]: v } });
  const setUploads = (groupKey, files) => onChange({ ...evidence, uploads: { ...uploads, [groupKey]: files } });

  const conditionalFields = FIELDS_BY_OFFER[type] || [];

  return (
    <div data-testid="offer-evidence-section" style={{ marginTop: 24, borderTop: `1px solid ${theme.colors.borders}`, paddingTop: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 6 }}>
        Kickback Item / Offer Evidence
      </h3>
      <p style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 14 }}>
        Add proof materials that help describe, verify, or support the product, service, company, or offer connected to this Kickback. These materials help create trust, collector value, and a clearer launch record.
      </p>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 6 }}>
          Offer Type
        </label>
        <select
          data-testid="offer-type-select"
          value={type}
          onChange={(e) => setType(e.target.value)}
          style={{
            width: '100%', padding: '10px 12px',
            border: `1px solid ${theme.colors.borders}`,
            borderRadius: theme.radius.sm, background: '#FFF',
            fontSize: 13, color: theme.colors.text.primary,
          }}>
          <option value="">— Select offer type —</option>
          {OFFER_TYPES.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {conditionalFields.length > 0 && (
        <div data-testid={`offer-fields-${type}`} style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 20,
        }}>
          {conditionalFields.map((f) => (
            <div key={f.key} style={{ gridColumn: f.type === 'textarea' ? '1 / -1' : 'auto' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 4 }}>
                {f.label}
              </label>
              {f.type === 'textarea' ? (
                <textarea
                  data-testid={`offer-field-${f.key}`}
                  rows={3}
                  value={fields[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  style={{
                    width: '100%', padding: '10px', border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm, background: '#FFF', fontSize: 13,
                    color: theme.colors.text.primary, resize: 'vertical',
                  }}
                />
              ) : (
                <input
                  data-testid={`offer-field-${f.key}`}
                  type="text"
                  value={fields[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  style={{
                    width: '100%', padding: '10px', border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm, background: '#FFF', fontSize: 13,
                    color: theme.colors.text.primary,
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div>
        <h4 style={{
          fontSize: 13, fontWeight: 700, color: theme.colors.text.primary,
          marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <UploadCloud size={14} /> Evidence uploads
        </h4>
        <p style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 10 }}>
          JPG, PNG, WebP, or PDF. These files are stored with your Kickback and are separate from Step 2 artwork.
        </p>
        {UPLOAD_GROUPS.map((g) => (
          <GroupUploader
            key={g.key}
            group={g}
            files={uploads[g.key] || []}
            onChange={(next) => setUploads(g.key, next)}
          />
        ))}
      </div>
    </div>
  );
}
